import { FormControl, Grid, TextField } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { SERVER_URL } from '../../config';
import axios from 'axios';
import validator from 'validator'
import _ from 'lodash';
import './UserDetails.scss';
import { isPhone } from '../../services/deviceService';
import { validateEnglandWalesPostcode } from './PostCodeValidator';

import { useAppSelector } from '../../app/hooks';
import { selectAffiliate } from '../../features/affiliate/affiliateSlice';

interface UserDetailsProps {
  setSubmitting(value: boolean): void;
};

const UserDetails = (props: UserDetailsProps, ref: any) => {
  const [user, setUser] = React.useState({ dateOfBirth: null } as any);
  const [showErrors, setShowErrors] = React.useState(false);
  const affiliate = useAppSelector(selectAffiliate);

  var [validationErrors, setValidationErrors] = React.useState({} as any);

  const setAffiliate = async () => {
    if (affiliate)
      setUser({ ...user, affiliateId: affiliate.affiliateId });
  }

  useEffect(() => {
    setAffiliate();
  }, []);

  useImperativeHandle(ref, () => ({
    async next() {
      setShowErrors(true);
      validate();
      const isValid = checkValid();
      if (!isValid) {
        return;
      }
      window.scroll(0, -4000);
      props.setSubmitting(true);

      if (await validateEmailAddressUniques()) {
        const config = {};

        if (user.dateOfBirth) {
          const timeOfBirth = new Date().getTime() - user.dateOfBirth.getTime();
          if (isNaN(timeOfBirth)) {
            setValidationErrors({ dateOfBirth: 'Date of birth is invalid.' });
            props.setSubmitting(false);
            return false;
          }
          if (timeOfBirth < 18 * 31556952000) {
            setValidationErrors({ dateOfBirth: 'Date of birth should be 18 or above' });
            props.setSubmitting(false);
            return false;
          }
        }
        const url = SERVER_URL + "/user";
        await axios.post(url, user, config);
        props.setSubmitting(false);
        return user;
      }
      else {
        props.setSubmitting(false);
        return false;
      }
    }
  }));

  const handleDateChange = (date: any) => {
    user.dateOfBirth = date;
    setUser({ ...user });
    validate();
  };

  const handleUserChange = (event: any) => {
    
    var value = event.target.name == "postcode"
      ? event.target.value.toUpperCase()
      : event.target.value;

    user[(event.target.name as string)] = value;
    
    setUser({ ...user });

    validate();
  }

  const validateEmailAddressUniques = async () => {

    var emailExists = await axios.get(SERVER_URL + "/useremail", { params: { email: user.email } });

    if (emailExists && emailExists.data) {
      validationErrors.email = "Email address is already used";
      setValidationErrors({ ...validationErrors });
      return false;
    }

    return true;
  }

  const checkValid = () => {
    if (!user.name) return false;

    if (!user.dateOfBirth)
      return false;

    if (!user.email)
      return false;

    if (user.email && !validator.isEmail(user.email))
      return false;

    if (!user.confirmEmail)
      return false;

    if (user.confirmEmail && !validator.isEmail(user.confirmEmail))
      return false;

    if (user.email !== user.confirmEmail)
      return false;

    if (!user.address1)
      return false;

    if (!user.town)
      return false;

    if (!user.postcode) {
      return false;
    } else {
      var isPostCodeValid = validateEnglandWalesPostcode(user.postcode);
      if (!isPostCodeValid) {
        return false;
      }
    }
    return true;
  }

  const validate = () => {
    validationErrors = {};

    if (!user.name) {
      validationErrors.name = "Name is required";
    }


    if (!user.dateOfBirth)
      validationErrors.dateOfBirth = "Date of birth is required";

    if (user.dateOfBirth) {
      const timeOfBirth = new Date().getTime() - user.dateOfBirth.getTime();
      if (isNaN(timeOfBirth)) {
        validationErrors.dateOfBirth = 'Date of birth is invalid.';
      }
      if (timeOfBirth < 18 * 31556952000) {
        validationErrors.dateOfBirth = 'Date of birth should be 18 or above';
      }
    }

    if (!user.email)
      validationErrors.email = "Email address is required";

    if (user.email && !validator.isEmail(user.email))
      validationErrors.email = "Email address is not valid";

    if (!user.confirmEmail)
      validationErrors.confirmEmail = "Email address is required";

    if (user.confirmEmail && !validator.isEmail(user.confirmEmail))
      validationErrors.confirmEmail = "Email address is not valid";

    if (user.email !== user.confirmEmail)
      validationErrors.confirmEmail = "Email addresses do not match";

    if (!user.address1)
      validationErrors.address1 = "Address is required";

    if (!user.town)
      validationErrors.town = "Town is required";

    if (!user.postcode) {
      validationErrors.postcode = "Postcode is required";
    } else {
      var isValid = validateEnglandWalesPostcode(user.postcode);
      if (!isValid) {
        validationErrors.postcode = "Postcode is not valid";
      }
    }

    setValidationErrors({ ...validationErrors });
  }


  useEffect(() => {
    var dd = document.getElementById('date-picker-inline-label');
    if (dd) {
      dd.remove();
    }
  }, [])

  return (
    <form className="UserDetailsForm">
      <div className="UserDetails" data-testid="UserDetails">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          {isPhone() ?
            <div className="details-box">
              <Grid container item xs={12}>
                <TextField id="standard-full-width"
                  autoComplete="name"
                  required
                  helperText={showErrors ? validationErrors.name : ''}
                  error={showErrors ? validationErrors.name : false}
                  name="name"
                  value={user.name}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Name" placeholder="Enter your full name" variant="outlined" />
                <div className="MuiFormControl-root">
                  <div className="input-label">Date of Birth</div>
                  <KeyboardDatePicker
                    name="dateOfBirth"
                    inputVariant="outlined"
                    disableToolbar
                    variant="inline"
                    className="date-of-birth"
                    format="dd/MM/yyyy"
                    margin="normal"
                    placeholder="DD/MM/YYYY"
                    id="date-picker-inline"
                    label="Date of birth"
                    value={user.dateOfBirth}
                    helperText={showErrors ? validationErrors.dateOfBirth : ''}
                    error={showErrors ? validationErrors.dateOfBirth : false}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                      'aria-label': 'change date',
                    }} />
                </div>
                <TextField id="outlined-search"
                  required
                  name="address1"
                  value={user.address1}
                  helperText={showErrors ? validationErrors.address1 : ''}
                  error={showErrors ? validationErrors.address1 : false}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }}
                  label="Building and street"
                  className="line-address"
                  placeholder="Enter line address 1"
                  variant="outlined" />
                <TextField
                  name="address2"
                  value={user.address2}
                  onChange={handleUserChange}
                  className="line-address second-line-address"
                  id="outlined-search"
                  placeholder="Enter line address 2"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined" />
                <TextField id="outlined-search" required
                  name="town"
                  value={user.town}
                  helperText={showErrors ? validationErrors.town : ''}
                  error={showErrors ? validationErrors.town : false}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Town or city" placeholder="Enter your town or city" variant="outlined" />
                <TextField id="outlined-search"
                  name="county"
                  value={user.county}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="County" placeholder="Enter your country" variant="outlined" />
                <TextField id="outlined-search"
                  name="postcode"
                  value={user.postcode}
                  helperText={showErrors ? validationErrors.postcode : ''}
                  error={showErrors ? validationErrors.postcode : false}
                  onChange={handleUserChange}
                  required InputLabelProps={{ shrink: true }} label="Postcode" placeholder="Enter your zip code" variant="outlined" />
                <TextField id="standard-full-width"
                  required
                  type="email"
                  name="email"
                  helperText={showErrors ? validationErrors.email : ''}
                  error={showErrors ? validationErrors.email : false}
                  value={user.email}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Email" placeholder="Enter your email" variant="outlined" />
                <TextField id="standard-full-width"
                  required
                  type="email"
                  name="confirmEmail"
                  helperText={showErrors ? validationErrors.confirmEmail : ''}
                  error={showErrors ? validationErrors.confirmEmail : false}
                  value={user.confirmEmail}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Confirm Email" variant="outlined" />
                {affiliate?.affiliateName &&
                  <FormControl>
                    <label><b>{affiliate.affiliateName}</b> would love to know your name and email - are you happy to share?</label>
                    <ToggleButtonGroup value={user.isAnonymous}
                      size="large"
                      exclusive
                      onChange={(event, value) => {
                        setUser({ ...user, isAnonymous: value })
                      }}
                    >
                      <ToggleButton value={0}>Yes</ToggleButton>
                      <ToggleButton value={1}>No</ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                }
              </Grid>
            </div>
            : <div className="details-box">
              <Grid container item xs={12} md={6} lg={4} className="start">
                <TextField id="standard-full-width"
                  required
                  helperText={showErrors ? validationErrors.name : ''}
                  error={showErrors ? validationErrors.name : false}
                  name="name"
                  autoComplete="name"
                  value={user.name}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Name" placeholder="Enter your full name" variant="outlined" />
                <div className="MuiFormControl-root">
                  <div className="input-label">Date of Birth</div>
                  <KeyboardDatePicker
                    name="dateOfBirth"
                    inputVariant="outlined"
                    disableToolbar
                    variant="inline"
                    className="date-of-birth"
                    format="dd/MM/yyyy"
                    margin="normal"
                    placeholder="DD/MM/YYYY"
                    id="date-picker-inline"
                    label="Date of birth"
                    value={user.dateOfBirth}
                    helperText={showErrors ? validationErrors.dateOfBirth : ''}
                    error={showErrors ? validationErrors.dateOfBirth : false}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                      'aria-label': 'change date',
                    }} />
                </div>
                <TextField id="standard-full-width"
                  required
                  type="email"
                  name="email"
                  helperText={showErrors ? validationErrors.email : ''}
                  error={showErrors ? validationErrors.email : false}
                  value={user.email}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Email" placeholder="Enter your email" variant="outlined" />
                <TextField id="standard-full-width"
                  required
                  type="email"
                  name="confirmEmail"
                  helperText={showErrors ? validationErrors.confirmEmail : ''}
                  error={showErrors ? validationErrors.confirmEmail : false}
                  value={user.confirmEmail}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Confirm Email" variant="outlined" />

                {affiliate?.affiliateName &&
                  <FormControl>
                    <label><b>{affiliate.affiliateName}</b> would love to know your name and email - are you happy to share?</label>
                    <ToggleButtonGroup value={user.isAnonymous}
                      size="large"
                      exclusive
                      onChange={(event, value) => {
                        setUser({ ...user, isAnonymous: value })
                      }}
                    >
                      <ToggleButton value={0}>Yes</ToggleButton>
                      <ToggleButton value={1}>No</ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                }
              </Grid>
              <Grid container item xs={12} md={6} lg={4} className="end">
                <TextField id="outlined-search"
                  required
                  name="address1"
                  value={user.address1}
                  helperText={showErrors ? validationErrors.address1 : ''}
                  error={showErrors ? validationErrors.address1 : false}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }}
                  label="Building and street"
                  className="line-address"
                  placeholder="Enter line address 1"
                  variant="outlined" />
                <TextField
                  name="address2"
                  value={user.address2}
                  onChange={handleUserChange}
                  className="line-address second-line-address"
                  id="outlined-search"
                  placeholder="Enter line address 2"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined" />
                <TextField id="outlined-search" required
                  name="town"
                  value={user.town}
                  className="user-shorter-field"
                  helperText={showErrors ? validationErrors.town : ''}
                  error={showErrors ? validationErrors.town : false}
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="Town or city" placeholder="Enter your town or city" variant="outlined" />
                <TextField id="outlined-search"
                  name="county"
                  value={user.county}
                  className="user-shorter-field"
                  onChange={handleUserChange}
                  InputLabelProps={{ shrink: true }} label="County" placeholder="Enter your country" variant="outlined" />
                <TextField id="outlined-search"
                  type="text"
                  name="postcode"

                  autoComplete="postal-code"

                  value={user.postcode}
                  className="user-shorter-field postcode"
                  helperText={showErrors ? validationErrors.postcode : ''}
                  error={showErrors ? validationErrors.postcode : false}
                  onChange={handleUserChange}
                  required InputLabelProps={{ shrink: true }} label="Postcode" placeholder="Enter your zip code" variant="outlined" />
              </Grid>
            </div>}
        </MuiPickersUtilsProvider>
      </div>
    </form >
  );
};

export default forwardRef(UserDetails);
